import { useCallback, useEffect, useState } from 'react';

export function useLocalStorage<T>(
    key: string,
    initialValue: T,
    onChange?: (value: T) => void
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
    const readValue = useCallback((): T => {
        if (typeof window === 'undefined') {
            return initialValue;
        }

        try {
            const item = window.localStorage.getItem(key);
            if (item === null) {
                return initialValue;
            }

            try {
                return JSON.parse(item) as T;
            } catch {
                return item as T;
            }
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
    }, [key, initialValue]);

    const [storedValue, setStoredValue] = useState<T>(readValue);

    const setValue = useCallback(
        (value: T | ((prev: T) => T)) => {
            if (typeof window === 'undefined') {
                console.warn(`Tried to set localStorage key "${key}" with window undefined`);
                return;
            }

            try {
                const newValue = value instanceof Function ? value(storedValue) : value;

                setStoredValue(newValue);

                if (newValue === undefined || newValue === null) {
                    window.localStorage.removeItem(key);
                } else {
                    const valueToStore = typeof newValue === 'string' ? newValue : JSON.stringify(newValue);
                    window.localStorage.setItem(key, valueToStore);
                }

                if (onChange) {
                    onChange(newValue);
                }
            } catch (error) {
                console.error(`Error setting localStorage key "${key}":`, error);
            }
        },
        [key, storedValue, onChange]
    );

    const removeValue = useCallback(() => {
        if (typeof window === 'undefined') {
            console.warn(`Tried to remove localStorage key "${key}" with window undefined`);
            return;
        }

        try {
            window.localStorage.removeItem(key);
            setStoredValue(initialValue);

            if (onChange) {
                onChange(initialValue);
            }

        } catch (error) {
            console.error(`Error removing localStorage key "${key}":`, error);
        }
    }, [key, initialValue, onChange]);

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key !== key || e.storageArea !== window.localStorage) {
                return;
            }

            try {
                let newValue: T;
                if (e.newValue === null) {
                    newValue = initialValue;
                    setStoredValue(initialValue);
                } else {
                    try {
                        newValue = JSON.parse(e.newValue) as T;
                        setStoredValue(newValue);
                    } catch {
                        newValue = e.newValue as T;
                        setStoredValue(newValue);
                    }
                }

                if (onChange) {
                    onChange(newValue);
                }
            } catch (error) {
                console.error(`Error handling storage change for key "${key}":`, error);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [key, initialValue, onChange]);

    return [storedValue, setValue, removeValue];
}

export default useLocalStorage;
